import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import Button from "@/components/shared/Button";

import FormField from "./FormField";
import TextAreaField from "./TextAreaField";
import { useContact } from "@/hooks/useContact";

const contactSchema = z.object({
  firstName: z.string().min(2, "First name is required"),
  lastName: z.string().min(2, "Last name is required"),
  email: z.email("Invalid email"),
  phone: z.string().min(6, "Phone number is required"),
  message: z.string().min(10, "Message is required"),
});

const ContactForm = ({ isAR }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(contactSchema),
  });

  const { submitContact, loading } = useContact();

  const onSubmit = async (data) => {
    const payload = {
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      phone: data.phone,
      message: data.message,
    };

    try {
      const result = await submitContact(payload);

      toast.success(
        isAR
          ? "تم إرسال الرسالة بنجاح"
          : result?.message || "Message sent successfully",
      );

      reset();
    } catch (err) {
      toast.error(
        isAR
          ? "حدث خطأ أثناء إرسال الرسالة"
          : err?.response?.data?.message ||
              err?.message ||
              "Failed to send message",
      );
    }
  };

  return (
    <div className="bg-white p-10 md:col-span-3">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Row */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            label={isAR ? "الاسم الأول" : "First Name"}
            name="firstName"
            placeholder={isAR ? "أدخل الاسم الأول" : "Enter first name"}
            register={register}
            error={errors.firstName?.message}
          />

          <FormField
            label={isAR ? "اسم العائلة" : "Last Name"}
            name="lastName"
            placeholder={isAR ? "أدخل اسم العائلة" : "Enter last name"}
            register={register}
            error={errors.lastName?.message}
          />
        </div>

        {/* Row */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            label={isAR ? "البريد الإلكتروني" : "Email"}
            name="email"
            type="email"
            placeholder="example@email.com"
            register={register}
            error={errors.email?.message}
          />

          <FormField
            label={isAR ? "رقم الهاتف" : "Phone"}
            name="phone"
            placeholder={isAR ? "أدخل رقم الهاتف" : "Enter phone number"}
            register={register}
            error={errors.phone?.message}
          />
        </div>

        <TextAreaField
          label={isAR ? "الرسالة" : "Message"}
          name="message"
          placeholder={
            isAR ? "اكتب رسالتك هنا..." : "Write your message here..."
          }
          register={register}
          error={errors.message?.message}
        />

        <Button
          type="submit"
          variant="primary"
          disabled={loading}
          className="mt-4 w-full px-8 disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
        >
          {loading
            ? isAR
              ? "جارٍ الإرسال..."
              : "Sending..."
            : isAR
              ? "إرسال"
              : "Send Message"}
        </Button>
      </form>
    </div>
  );
};

export default ContactForm;
